/**
 * Typography Component
 * Adapted from web team's "next" project for Vortex
 *
 * Provides a consistent typography system with predefined sizes and appearances.
 */
import * as React from "react";
import { createElement } from "react";
import { joinClasses } from "../../utils/joinClasses";
import { Icon } from "../icon/Icon";
const typeFallbacks = {
    div: "body-md",
    h1: "heading-2xl",
    h2: "heading-xl",
    h3: "heading-lg",
    h4: "heading-md",
    h5: "heading-sm",
    h6: "heading-xs",
    p: "body-md",
    span: "body-md",
    ul: "body-md",
};
const toClasses = (type, prefix = "") => {
    const classes = [`${prefix}text-${type}`];
    if (type.startsWith("title-")) {
        classes.push(`${prefix}uppercase`);
    }
    return classes;
};
const getTypographyStyles = ({ as = "span", typographyType, }) => {
    if (typeof typographyType === "object") {
        return Object.entries(typographyType).flatMap(([size, type]) => !type ? [] : toClasses(type, size === "default" ? "" : `${size}:`));
    }
    return toClasses(typographyType ?? typeFallbacks[as]);
};
export const Typography = ({ appearance = "strong", as = "p", children, className, isTranslucent = false, typographyType, ...props }) => {
    const appearanceClasses = {
        none: "",
        inverted: isTranslucent
            ? "text-translucent-dark-950"
            : "text-neutral-inverted",
        moderate: isTranslucent
            ? "text-neutral-translucent-moderate"
            : "text-neutral-moderate",
        strong: isTranslucent
            ? "text-neutral-translucent-strong"
            : "text-neutral-strong",
        subdued: isTranslucent
            ? "text-neutral-translucent-subdued"
            : "text-neutral-subdued",
        weak: isTranslucent ? "text-neutral-translucent-weak" : "text-neutral-weak",
    };
    return createElement(as, {
        className: joinClasses([
            ...getTypographyStyles({ as, typographyType }),
            appearanceClasses[appearance],
            className,
        ]),
        ...props,
    }, children);
};
const LinkContent = ({ iconSize, label, leftIconPath, rightIconPath, }) => (React.createElement(React.Fragment, null,
    !!leftIconPath && (React.createElement(Icon, { className: "shrink-0", path: leftIconPath, size: iconSize })),
    label,
    !!rightIconPath && (React.createElement(Icon, { className: "shrink-0", path: rightIconPath, size: iconSize }))));
export const TypographyLink = ({ appearance = "strong", "aria-disabled": ariaDisabled, as = "a", children, className: additionalClasses = "", customContent, disabled, href, iconSize, isExternal, leftIconPath, ref, rightIconPath, typographyType = "body-md", variant = "primary", ...props }) => {
    const variantClasses = {
        none: "",
        primary: "nxm-link-variant-primary",
        secondary: "nxm-link-variant-secondary",
    };
    /* eslint-disable sort-keys */
    const appearanceClasses = {
        none: "",
        info: "nxm-link-info",
        premium: "nxm-link-premium",
        primary: "nxm-link-primary",
        moderate: "nxm-link-moderate",
        strong: "nxm-link-strong",
        subdued: "nxm-link-subdued",
    };
    /* eslint-enable sort-keys */
    const className = joinClasses([
        "nxm-link",
        variantClasses[variant],
        appearanceClasses[appearance],
        ...(typographyType !== "inherit"
            ? getTypographyStyles({
                typographyType: typographyType,
            })
            : []),
        additionalClasses,
    ], {
        "nxm-link-disabled": ariaDisabled === true || ariaDisabled === "true" || disabled,
    });
    return createElement(as, {
        className,
        ref,
        ...(as === "a"
            ? {
                href,
                ...(isExternal ? { rel: "noreferrer", target: "_blank" } : {}),
            }
            : { disabled }),
        ...props,
    }, React.createElement(React.Fragment, null, customContent ?? (React.createElement(LinkContent, { iconSize: iconSize, label: children, leftIconPath: leftIconPath, rightIconPath: rightIconPath }))));
};
